import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDiary, formatTime } from './DiaryContext';
import { canPostFreeTimes } from '../marketing-v2/features/social/data/fromDiary';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * StaffHeader - Sticky-top header for each staff column.
 * Shows avatar (or initials fallback), name, and working hours.
 * Clicking anywhere on the header opens a popup with Edit Shift / View Week
 * (and Post Free Times, when Social is on and this person can use it).
 */
function StaffHeader({ staff }) {
  const [imgError, setImgError] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const headerRef = useRef(null);
  const { onStaffHeaderAction } = useDiary();

  if (!staff) return null;

  // A room's header: colour dot instead of avatar, group + turnover instead
  // of shift hours, no Edit Shift popup — rooms don't have shifts.
  if (staff.isResource) {
    return (
      <div className="dv2-staff-header dv2-staff-header--resource" ref={headerRef}>
        {staff.avatar
          ? <img className="dv2-res-thumb" src={staff.avatar} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
          : <span className="dv2-res-dot" style={{ background: staff.diaryColour }} />}
        <div className="dv2-staff-header-text">
          <div className="dv2-staff-name">{staff.name}</div>
          <div className="dv2-staff-hours">
            {!staff.isWorking
              ? 'Closed today'
              : staff.hasCustomHours
                ? `${formatTime(staff.startMinutes)} – ${formatTime(staff.endMinutes)}${staff.turnoverTime > 0 ? ` · ${staff.turnoverTime}m reset` : ''}`
                : `${staff.groupName}${staff.turnoverTime > 0 ? ` · ${staff.turnoverTime}m reset` : ''}`}
          </div>
        </div>
      </div>
    );
  }

  const hours = staff.isWorking
    ? `${formatTime(staff.startMinutes)} - ${formatTime(staff.endMinutes)}`
    : 'Not working';

  const hasAvatar = staff.avatar && !imgError;

  const handleHeaderClick = (e) => {
    e.stopPropagation();
    setPopupOpen(prev => !prev);
  };

  const handleAction = (action) => {
    setPopupOpen(false);
    if (onStaffHeaderAction) onStaffHeaderAction(staff, action);
  };

  return (
    <div
      ref={headerRef}
      className={`dv2-staff-header dv2-staff-header--interactive ${!staff.isWorking ? 'dv2-staff-header--off' : ''}`}
      onClick={handleHeaderClick}
    >
      {hasAvatar ? (
        <img
          className="dv2-staff-avatar"
          src={staff.avatar}
          alt={staff.name}
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="dv2-staff-avatar dv2-staff-avatar--initials">
          {getInitials(staff.name)}
        </div>
      )}
      <span className="dv2-staff-name">{staff.name}</span>
      {staff.raw && staff.raw.isOnboardingDemo
        ? <span className="dv2-staff-example">{staff.raw.demoLabel || 'Example'}</span>
        : <span className="dv2-staff-hours">{hours}</span>}
      {popupOpen && (
        <StaffHeaderPopup
          anchorRef={headerRef}
          onAction={handleAction}
          onClose={() => setPopupOpen(false)}
          canPost={!(staff.raw && staff.raw.isOnboardingDemo) && canPostFreeTimes(staff.id)}
        />
      )}
    </div>
  );
}

/**
 * StaffHeaderPopup - Small popover anchored below the staff header.
 */
function StaffHeaderPopup({ anchorRef, onAction, onClose, canPost }) {
  const popupRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Close on any click outside the popup (catches clicks that stopPropagation blocks from the overlay)
  useEffect(() => {
    const handleDocClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Use timeout so the opening click doesn't immediately close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleDocClick, true);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleDocClick, true);
    };
  }, [onClose]);

  useEffect(() => {
    if (!anchorRef.current || !popupRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    const pad = 8;

    let left = anchorRect.left + anchorRect.width / 2 - popupRect.width / 2;
    let top = anchorRect.bottom + 4;

    // Clamp to viewport
    if (top + popupRect.height > window.innerHeight - pad) {
      top = anchorRect.top - popupRect.height - 4;
    }
    if (top < pad) top = pad;
    if (left < pad) left = pad;
    if (left + popupRect.width > window.innerWidth - pad) {
      left = window.innerWidth - pad - popupRect.width;
    }

    setPos({ left, top });
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div className="dv2-staff-popup-overlay" onClick={onClose}>
      <div
        ref={popupRef}
        className="dv2-staff-popup"
        style={pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden', left: 0, top: 0 }}
        onClick={e => e.stopPropagation()}
      >
        <button className="dv2-staff-popup-item" onClick={() => onAction('editShift')}>
          <span className="dv2-staff-popup-icon">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="2" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M2 7H16" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M6 1.5V4M12 1.5V4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              <path d="M5.5 10.5H8.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </span>
          Edit Shift
        </button>
        <button className="dv2-staff-popup-item" onClick={() => onAction('viewWeek')}>
          <span className="dv2-staff-popup-icon">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="2" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M2 7H16" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M6 3V15M10 3V15" stroke="currentColor" strokeWidth="1.3"/>
            </svg>
          </span>
          View Week
        </button>
        {canPost && (
          <button className="dv2-staff-popup-item" onClick={() => onAction('postFreeTimes')}>
            <span className="dv2-staff-popup-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M3.5 7H5.5L11 4V14L5.5 11H3.5C3.2 11 3 10.8 3 10.5V7.5C3 7.2 3.2 7 3.5 7Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                <path d="M5.5 11L6.5 14.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                <path d="M13.5 6.8C14.2 7.4 14.6 8.2 14.6 9C14.6 9.8 14.2 10.6 13.5 11.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </span>
            Post Free Times
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}

export default React.memo(StaffHeader);
